import { render, h } from 'preact';
import type { BitioBuilderConfig } from '@bitio-builder/shared';
import { BitioBuilder } from './components/BitioBuilder';

// Web Component wrapper for the widget
class BitioBuilderElement extends HTMLElement {
  private config: BitioBuilderConfig | null = null;
  private container: HTMLDivElement | null = null;

  connectedCallback() {
    // Get configuration from data attributes
    const apiKey = this.getAttribute('data-api-key') || '';
    const apiUrl = this.getAttribute('data-api-url') || undefined;
    const title = this.getAttribute('data-title') || undefined;
    const position = (this.getAttribute('data-position') as 'bottom-right' | 'bottom-left') || undefined;
    const language = (this.getAttribute('data-language') as 'sv' | 'en') || undefined;
    const primaryColor = this.getAttribute('data-primary-color') || undefined;
    const accentColor = this.getAttribute('data-accent-color') || undefined;
    const backgroundColor = this.getAttribute('data-background-color') || undefined;

    // Office hours configuration
    const officeHoursDays = this.getAttribute('data-office-hours-days');
    const officeHoursStart = this.getAttribute('data-office-hours-start');
    const officeHoursEnd = this.getAttribute('data-office-hours-end');
    const officeHoursTimezone = this.getAttribute('data-office-hours-timezone');
    const noReplyTimeoutStr = this.getAttribute('data-no-reply-timeout');

    if (!apiKey) {
      console.error('Bitio Builder: Missing data-api-key attribute');
      return;
    }

    // Parse office hours if any field is provided
    let officeHours = undefined;
    if (officeHoursDays || officeHoursStart || officeHoursEnd || officeHoursTimezone) {
      officeHours = {
        days: officeHoursDays ? officeHoursDays.split(',').map(d => parseInt(d.trim(), 10)) : [1, 2, 3, 4, 5],
        startTime: officeHoursStart || '09:00',
        endTime: officeHoursEnd || '17:00',
        timezone: officeHoursTimezone || 'Europe/Stockholm',
      };
    }

    const noReplyTimeout = noReplyTimeoutStr ? parseInt(noReplyTimeoutStr, 10) : undefined;

    this.config = {
      apiKey,
      apiUrl,
      title,
      position,
      language,
      primaryColor,
      accentColor,
      backgroundColor,
      officeHours,
      noReplyTimeout,
    };

    // Create container in body (not in custom element)
    this.container = document.createElement('div');
    this.container.id = 'bitio-builder-root';
    document.body.appendChild(this.container);

    // Render Preact component
    render(h(BitioBuilder, { config: this.config }), this.container);
  }

  disconnectedCallback() {
    if (this.container) {
      render(null, this.container);
      this.container.remove();
    }
  }
}

// Register custom element
if (!customElements.get('bitio-builder')) {
  customElements.define('bitio-builder', BitioBuilderElement);
}

// Auto-initialize from script tag
function autoInitialize() {
  const scripts = document.querySelectorAll('script[data-api-key]');

  scripts.forEach((script) => {
    const apiKey = script.getAttribute('data-api-key');
    if (!apiKey) return;

    if (document.querySelector('bitio-builder')) return;

    const widget = document.createElement('bitio-builder');
    widget.setAttribute('data-api-key', apiKey);

    const attrs = [
      'api-url', 'title', 'position', 'language', 'primary-color', 'accent-color', 'background-color',
      'office-hours-days', 'office-hours-start', 'office-hours-end', 'office-hours-timezone',
      'no-reply-timeout'
    ];
    attrs.forEach((attr) => {
      const value = script.getAttribute(`data-${attr}`);
      if (value) {
        widget.setAttribute(`data-${attr}`, value);
      }
    });

    document.body.appendChild(widget);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoInitialize);
} else {
  autoInitialize();
}

export { BitioBuilder, BitioBuilderElement };
export type { BitioBuilderConfig };

declare global {
  interface Window {
    BitioBuilder: {
      init: (config: BitioBuilderConfig) => void;
    };
  }
}

window.BitioBuilder = {
  init: (config: BitioBuilderConfig) => {
    const widget = document.createElement('bitio-builder');
    widget.setAttribute('data-api-key', config.apiKey);

    if (config.apiUrl) widget.setAttribute('data-api-url', config.apiUrl);
    if (config.title) widget.setAttribute('data-title', config.title);
    if (config.position) widget.setAttribute('data-position', config.position);
    if (config.language) widget.setAttribute('data-language', config.language);
    if (config.primaryColor) widget.setAttribute('data-primary-color', config.primaryColor);
    if (config.accentColor) widget.setAttribute('data-accent-color', config.accentColor);
    if (config.backgroundColor) widget.setAttribute('data-background-color', config.backgroundColor);

    // Office hours configuration
    if (config.officeHours) {
      widget.setAttribute('data-office-hours-days', config.officeHours.days.join(','));
      widget.setAttribute('data-office-hours-start', config.officeHours.startTime);
      widget.setAttribute('data-office-hours-end', config.officeHours.endTime);
      widget.setAttribute('data-office-hours-timezone', config.officeHours.timezone);
    }
    if (config.noReplyTimeout !== undefined) {
      widget.setAttribute('data-no-reply-timeout', String(config.noReplyTimeout));
    }

    document.body.appendChild(widget);
  },
};
